import { PaymentMethod, Token } from '@stripe/stripe-js';
import {
    v2CreatePaymentMethodPath,
    v2ValidateUserCreditCardExpirationDatePath,
    v2SetupUserPlaidTokenPath,
    v2CreateChargeableBankAccountPath
} from '../../../utils/ts-routes.utils';
import {
    IInitiatePaymentProps,
    IInitPaymentActionResponse,
    PaymentMethodType,
    ISetPaymentAsChargedProps
} from '../../../types/pay.types';
import {
    IPaymentMethodResponse,
    IBankAccountResponse,
    IPlaidAccount,
    AutoPayStatus,
    IFlowUpdatedResponse
} from '../../../types/server.types';
import { IOnEventMetadata } from '../../common/Stripe/usePlaid';
import { AbstractPaymentMethodService } from '~/components/ClientExperience/services/abstract-payment-method.service';

export interface ICreatePaymentMethod {
    userId: string;
    vendorId: string;
    stripeToken?: Token;
    paymentMethodType: PaymentMethodType;
    cardRole: string;
    flowId: string;
    stripePaymentMethod?: PaymentMethod;
}

export interface IValidatePaymentMethodData {
    forter_token: string;
    paid_in_person: boolean;
    payment_tip: number;
}

export interface IValidatePaymentMethod {
    paymentId: string;
    flowId: string;
    paymentMethodId: string;
    forterToken: string;
    gratuity: number;
}

export interface IDeletePaymentMethod {
    userId: string;
    flowId: string;
    id: string;
}

export interface IPostPaymentActions {
    scheduling?: {
        page_id: string;
        variable_id: string;
        data: {
            selected_date: string;
            client_timezone: string;
            user_ids: string[];
        };
    };
}

export interface IPayMethodData {
    activate_auto_payment: boolean;
    activate_recurring_payment: boolean;
    forter_token: string;
    payment_tip: number;
    paid_in_person: boolean;
    revision_id: string;
}

export interface IPayMethod {
    paymentId: string;
    flowId: string;
    revisionId: string;
    paymentMethodId: string;
    forterToken: string;
    autoPay: boolean;
    gratuity: number;
    postPaymentActions: IPostPaymentActions;
}

export interface IValidatePaymentMethodExpirationData {
    month: string;
    year: string;
    paymentMethodId: string;
    flowId: string;
}

export interface ISetupBankAccount {
    userId: string;
    token: string;
    institutionId: string;
}

export interface ICreateChargeableBankAccount {
    userId: string;
    account: IBankAccountResponse | IPlaidAccount;
    bankRole?: string[];
    flowId: string;
    institutionId: string;
    institutionName: string;
    account_key: string;
}

export type IPlaidProduct = 'auth' | 'transactions';

export interface ICreateLinkToken {
    flowId: string;
    userId: string;
    plaidProducts: IPlaidProduct[];
    redirectUri?: string;
}

export interface ILinkTokenResponse {
    link_token: string;
}

export interface IGetPlaidAccounts {
    userId: string;
    src: string;
    institutionId: string;
    institutionName: string;
}

export interface IReportPlaidEvent {
    flowId: string;
    eventName: string;
    metadata: IOnEventMetadata;
    userId: string;
}

export interface IPaymentMethodForFlow {
    userId: string;
    flowId: string;
}

export interface IExchangePublicToken {
    flowId: string;
    userId: string;
    publicToken: string;
    institutionId: string;
    institutionName: string;
}

export class PayService extends AbstractPaymentMethodService {
    async validatePayment({
        paymentId,
        paymentMethodId,
        flowId,
        forterToken,
        gratuity
    }: IValidatePaymentMethod) {
        const data: IValidatePaymentMethodData = {
            paid_in_person: false,
            payment_tip: gratuity,
            forter_token: forterToken
        };

        const response = await this.apiService.apiRequest<{
            result: boolean;
        }>({
            method: 'post',
            url: `/api/v2/flow/${flowId}/payments/${paymentId}/payment_methods/${paymentMethodId}/validate`,
            data
        });

        return response.result;
    }

    async pay({
        paymentId,
        paymentMethodId,
        flowId,
        revisionId,
        forterToken,
        autoPay,
        gratuity,
        postPaymentActions
    }: IPayMethod): Promise<IFlowUpdatedResponse> {
        const paymentMethodData: IPayMethodData = {
            paid_in_person: false,
            payment_tip: gratuity,
            forter_token: forterToken,
            activate_auto_payment: autoPay,
            activate_recurring_payment: false, // TODO,
            revision_id: revisionId
        };

        const data = {
            ...paymentMethodData,
            post_payment_actions: postPaymentActions
        };

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        return this.apiService.apiRequest<IFlowUpdatedResponse>({
            method: 'post',
            url: `/api/v2/flow/${flowId}/payments/${paymentId}/payment_methods/${paymentMethodId}`,
            data
        });
    }

    async deletePaymentMethod({ id, flowId, userId }: IDeletePaymentMethod) {
        return this.apiService.apiRequest<{
            _id: string;
            payment_methods: IPaymentMethodResponse[];
        }>({
            method: 'delete',
            url: `/api/v2/flow/${flowId}/users/${userId}/stripe_card/${id}`,
            networkLabel: 'deletePaymentMethod'
        });
    }

    async validatePaymentMethodExpirationDate({
        flowId,
        paymentMethodId,
        month,
        year
    }: IValidatePaymentMethodExpirationData) {
        const data = {
            month,
            year
        };

        return this.apiService.apiRequest<boolean>({
            method: 'put',
            url: v2ValidateUserCreditCardExpirationDatePath(
                flowId,
                paymentMethodId
            ),
            data
        });
    }

    async setupBankAccount({
        institutionId,
        token,
        userId
    }: ISetupBankAccount) {
        return this.apiService.apiRequest<IBankAccountResponse[]>({
            method: 'post',
            url: v2SetupUserPlaidTokenPath(userId),
            data: {
                token,
                institution_id: institutionId
            }
        });
    }

    async getUserPaymentMethodsForFlow({
        flowId,
        userId
    }: IPaymentMethodForFlow) {
        return this.apiService.apiRequest<IPaymentMethodResponse[]>({
            method: 'get',
            url: `/api/v2/flow/${userId}/payment_methods`,
            params: { flow_id: flowId }
        });
    }

    updateFlowAutoPay({
        flowId,
        status
    }: {
        flowId: string;
        status: AutoPayStatus;
    }) {
        return this.apiService.apiRequest<IFlowUpdatedResponse>({
            method: 'put',
            url: `/api/v2/flow/${flowId}/update_auto_payment`,
            data: { status }
        });
    }

    async initiatePayment({
        paymentId,
        flowId,
        revisionId,
        paymentMethodId,
        gratuity,
        activateAutoPay,
        postPaymentActions,
        forterToken
    }: Pick<
        IInitiatePaymentProps,
        'paymentId' | 'paymentMethodId' | 'gratuity' | 'activateAutoPay'
    > & {
        flowId: string;
        revisionId: string;
        postPaymentActions: IPostPaymentActions;
        forterToken: string;
    }): Promise<IInitPaymentActionResponse> {
        const data = {
            gratuity,
            revision_id: revisionId,
            payment_method_id: paymentMethodId,
            activate_auto_payment: activateAutoPay,
            post_payment_actions: postPaymentActions,
            forter_token: forterToken
        };
        return this.apiService.apiRequest<IInitPaymentActionResponse>({
            method: 'post',
            url: `/api/v2/flow/${flowId}/payments/${paymentId}`,
            data
        });
    }

    async setPaymentAsCharged({
        paymentId,
        chargeId,
        flowId,
        paymentMethodId,
        postPaymentActions,
        activateAutoPay
    }: Omit<ISetPaymentAsChargedProps, 'logger'> & {
        flowId: string;
        postPaymentActions: IPostPaymentActions;
    }): Promise<IFlowUpdatedResponse> {
        return this.apiService.apiRequest<IFlowUpdatedResponse>({
            method: 'put',
            url: `/api/v2/flow/${flowId}/payments/${paymentId}/charged`,
            data: {
                charge_id: chargeId,
                payment_method_id: paymentMethodId,
                activate_auto_payment: activateAutoPay,
                post_payment_actions: postPaymentActions
            }
        });
    }

    protected createChargeableBankAccountUrl = ({ flowId, userId }): string =>
        v2CreateChargeableBankAccountPath(flowId, userId);

    protected createLinkTokenUrl = ({ flowId, userId }): string =>
        `/api/v2/flow/${flowId}/users/${userId}/plaid/create_link_token`;

    protected createPaymentMethodUrl = ({ flowId, userId }): string =>
        v2CreatePaymentMethodPath(flowId, userId);

    protected exchangePublicTokenUrl = ({ flowId, userId }): string =>
        `/api/v2/flow/${flowId}/users/${userId}/plaid/exchange_public_token`;

    protected plaidEventUrl = ({ flowId, userId }): string =>
        `/api/v2/flow/${flowId}/users/${userId}/plaid/event`;
}
